// RedApp (Vodafone) screen-pop entry point.
//
// Standalone helper that reads the agent's RedApp process settings and fires the
// screen-pop for a connected call. It is called from wherever the CRM record has
// resolved and the agent is about to land on the CRM screen — on servers that
// route every caller through crmLanding.js's fetchCrmAndLand that is a single
// call site; on older servers with four divergent getCrmDetails copies it is
// called from each. Keeping the logic here (rather than inline) means those call
// sites only add one import and one line.
//
// It is deliberately self-contained and NEVER throws: a screen-pop must never
// prevent the agent from landing on the CRM, which is what actually lets them
// work the call. Every path is wrapped and reported through logs only.
//
// It is also a complete no-op unless the agent's PROCESS has RedApp turned on
// (Tenant Admin > Process > RedApp).

import { store } from '../store/store';
import { openRedappTab } from './redappTab';

// Master switch for the whole RedApp feature, driven by a BUILD-TIME env flag
// (REACT_APP_REDAPP_ENABLED in Web_Source/.env). When it is not explicitly
// enabled the feature does nothing at all — the screen-pop never fires and the
// Tenant Admin process toggle is hidden (AddEditProcessModal imports this too) —
// regardless of any per-process is_redapp value already saved in the DB.
//
// NOTE: REACT_APP_* vars are inlined at build time, so changing the .env value
// only takes effect after a rebuild. Enabled when the value is "true" or "yes"
// (case-insensitive); absent / "no" / "false" / anything else => disabled.
export const isRedappEnabled = () =>
  ['true', 'yes'].includes(
    String(process.env.REACT_APP_REDAPP_ENABLED || '').trim().toLowerCase()
  );

// Turns whatever was typed in the process setting into a real field_rank.
// Admins configure the field by its number, so "5" must mean custom_field_5.
// A full rank ("custom_field_5") or a fixed name ("primaryNumber") pass through.
const normalizeFieldRank = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  return /^\d+$/.test(raw) ? `custom_field_${raw}` : raw;
};

// Reads a dynamic CRM field's value out of the record. The CRM payload is an
// array (see crms_info): index 1 holds the fixed crmDetails, index 2 holds the
// dynamic `fields` list, each entry { field_rank, label_name, crm_value, ... }.
const readCrmField = (record, rank) =>
  record?.[2]?.fields?.find((field) => field.field_rank === rank)?.crm_value;

/**
 * Fires the RedApp screen-pop for a connected call.
 *
 * @param {Array}  record The CRM record (crms_info payload: response.data).
 * @param {Object} data   The call data ({ crm_id, unique_id }) for this call.
 * @param {Function} [log] Optional logger; console is used regardless.
 * @return {void}
 */
export const popRedapp = (record, data, log) => {
  try {
    // Master switch (build-time env flag). If RedApp was not enabled for this
    // build, do nothing at all — even for a process whose is_redapp is on.
    if (!isRedappEnabled()) return;

    // Settings come from the agent's PROCESS, delivered in the login response as
    // process_details. Read straight from the store so no caller has to change.
    // NOTE: not named `process` — that is a bundler-injected global.
    const settings = store.getState()?.auth?.userDetails?.process_details || {};

    console.log('[RedApp] pop called', {
      is_redapp: settings.is_redapp,
      redapp_url: settings.redapp_url,
      redapp_msisdn_field: settings.redapp_msisdn_field
    });

    // Absent (not false) when the agent's session predates this feature:
    // redux-persist replays the old login from localStorage, so the flag never
    // arrives until they log in again. Say so explicitly.
    if (settings.is_redapp === undefined) {
      console.warn(
        '[RedApp] process settings are missing from this session. Log out and log in again to pick them up.'
      );
      return;
    }

    // Toggle off for this process -> do nothing at all.
    if (!settings.is_redapp) return;

    if (!settings.redapp_url) {
      console.warn('[RedApp] enabled for this process but no RedApp URL is configured.');
      return;
    }

    const details = record?.[1]?.crmDetails;

    // RedApp is opened by ACCOUNT CODE (CustCode / FAID), not by phone number.
    // The value lives in the CRM field the admin configured on the process. The
    // setting is still named redapp_msisdn_field for backward compat, but it now
    // points at the account-code field (e.g. custom_field_1). Falls back to a
    // fixed crmDetails account key if no field is configured.
    const accountFieldRank = normalizeFieldRank(settings.redapp_msisdn_field);
    const accountID = accountFieldRank
      ? readCrmField(record, accountFieldRank)
      : details?.custCode || details?.CustCode || details?.accountID || details?.faid;

    // No account code for this call: fall back to the process's fallback url,
    // opened without a query string.
    const usingFallback = !accountID;
    const targetUrl = usingFallback ? settings.redapp_fallback_url : settings.redapp_url;

    if (usingFallback) {
      log?.(
        `RedApp account field "${accountFieldRank || '(crmDetails.custCode)'}" is empty for this record`
      );

      if (!targetUrl) {
        console.warn(
          '[RedApp] no accountID for this call and no fallback URL configured on this process.'
        );
        return;
      }
    }

    console.log('[RedApp] resolved accountID', {
      field: accountFieldRank || '(crmDetails.custCode)',
      accountID,
      usingFallback,
      url: targetUrl
    });

    openRedappTab({
      url: targetUrl,
      accountID,
      dialerUniqueID: data?.unique_id,
      log
    });
  } catch (error) {
    log?.(`RedApp screen-pop skipped: ${String(error)}`);
    console.warn('[RedApp] screen-pop skipped:', String(error));
  }
};
